import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { IconPlus } from '@tabler/icons';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import {
  useInternalAccounts,
  InternalAccount,
} from '../../data/useInternalAdmin';
import { canPerform } from '../../utils/internalPermissions';
import AQDataGrid, { AQDataGridQueryParams } from '../AQDataGrid';
import CreateAccountDialog from './CreateAccountDialog';

const InternalAccounts: React.FC = () => {
  const { user } = useAuth();
  const internalRole = user?.internal_role;
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [showDeactivated, setShowDeactivated] = React.useState(false);

  const buildQueryArgs = React.useCallback(
    (queryParams: AQDataGridQueryParams) => [
      {
        page: queryParams.page,
        size: queryParams.size,
        sort: queryParams.sort,
        showDeactivated,
      },
    ],
    [showDeactivated],
  );

  const rowsTransformer = React.useCallback((responseData: any) => {
    const accounts: InternalAccount[] = Array.isArray(responseData)
      ? responseData
      : responseData?.data || [];
    return accounts.map((acct) => ({
      id: acct.id,
      name: acct.attributes.name,
      title: acct.attributes.title || '-',
      is_internal_private: acct.attributes.is_internal_private,
      deactivated_at: acct.attributes.deactivated_at,
      created_at: acct.attributes.created_at,
    }));
  }, []);

  const colDefs = React.useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: true,
        filterable: true,
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
      },
      {
        field: 'is_internal_private',
        headerName: 'Type',
        width: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Chip
            label={params.value ? 'Internal' : 'Customer'}
            size="small"
            color={params.value ? 'default' : 'primary'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'deactivated_at',
        headerName: 'Status',
        width: 140,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Chip
            label={params.value ? 'Deactivated' : 'Active'}
            size="small"
            color={params.value ? 'error' : 'success'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 180,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(parseJSON(params.value), {
              addSuffix: true,
            })}
          </Typography>
        ),
      },
    ],
    [],
  );

  const gridProps = React.useMemo(
    () => ({
      rowHeight: 60,
      filterMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
      onRowClick: (params: any) => {
        navigate(`/internal/accounts/${params.id}`);
      },
      sx: { cursor: 'pointer' },
    }),
    [navigate],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Accounts</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDeactivated}
                onChange={(e) => setShowDeactivated(e.target.checked)}
              />
            }
            label="Show deactivated"
          />
          {canPerform(internalRole, 'create_account') && (
            <Button
              variant="contained"
              size="small"
              startIcon={<IconPlus size={16} />}
              onClick={() => setCreateOpen(true)}
            >
              Create Account
            </Button>
          )}
        </Box>
      </Box>

      <AQDataGrid
        id="internal-accounts"
        dataQueryHook={useInternalAccounts}
        buildQueryArgs={buildQueryArgs}
        rowsTransformer={rowsTransformer}
        colDefs={colDefs}
        gridProps={gridProps}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />

      <CreateAccountDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
};

export default InternalAccounts;
